import { isEmpty } from 'lodash-es';
import { Product } from '@lambda/apis/src/product/types';
import { NextRouter } from 'next/router';
import {
  EssentialSearchOffers,
  Options,
  SearchFilters,
  SearchInput,
} from '@/lib/collection-search-helpers';
import { CollectionsSearchApiResponse, ReducerAction } from './types';
import { useAbortableGetApi } from '@/lib/utils/use-abortable-api';

export const searchInputReducer = (
  state: SearchInput,
  action: ReducerAction,
): SearchInput => {
  if (action.type === 'reset') return { ...state, ...action.payload };

  if (action.type === 'sort') {
    return {
      ...state,
      sort: action.payload,
    };
  }

  if (action.type === 'page') {
    return {
      ...state,
      page: action.payload.value,
    };
  }

  if (action.type === 'minMaxPrice') {
    return {
      ...state,
      minPrice: action.payload.minPrice,
      maxPrice: action.payload.maxPrice,
    };
  }

  const variant = action.type;
  const { value, checked } = action.payload;

  const calculateInverseNewVariantValue = () => {
    const visibleFilters: { key: string }[] =
      action.payload?.visibleFilters ?? [];
    const selectedFilterValues: string[] =
      action.payload?.selectedFilterValues ?? [];
    const allFilterValues: string[] = action.payload?.allFilterValues ?? [];

    if (checked) {
      if (selectedFilterValues.length === allFilterValues.length)
        return [value];
      const allVisibleOptionsSelected =
        (visibleFilters?.length ?? 0) ===
          (selectedFilterValues.length ?? 0) + 1 &&
        (visibleFilters.every(
          (item) =>
            [value, ...selectedFilterValues].includes(item.key) ?? false,
        ) ??
          false);

      if (allVisibleOptionsSelected) return allFilterValues;

      return [value, ...selectedFilterValues];
    }
    if (
      selectedFilterValues.length === 1 &&
      selectedFilterValues.includes(value)
    )
      return allFilterValues;

    if (!checked && allFilterValues.length === selectedFilterValues.length) {
      return visibleFilters
        .filter((item) => item.key !== value)
        .map((item) => item.key);
    }

    return selectedFilterValues.filter((item) => item !== value);
  };

  const calculateDefaultNewVariantValue = () => {
    const existingVariantValue = state.variantFilters[variant] ?? [];
    const newVariantValue = checked
      ? [...existingVariantValue, value]
      : existingVariantValue.filter((item) => item !== value);

    return newVariantValue;
  };

  // special handling of reversed filters
  const newVariantValue = action.payload?.inverse
    ? calculateInverseNewVariantValue()
    : calculateDefaultNewVariantValue();

  const finalState = {
    ...state,
    variantFilters: {
      ...state.variantFilters,
      [variant]: newVariantValue.length > 0 ? newVariantValue : undefined,
    },
  };

  return finalState;
};

export const streamlineValue = (value: string) =>
  value.replace(/OPPO/g, 'Oppo');

export const generateSearchParams = (
  searchInput: SearchInput,
  fixedFilters: SearchFilters,
) => {
  const _searchParameters = searchInput?.variantFilters;

  if (isEmpty(_searchParameters)) return fixedFilters.variantFilters;

  const newEntries = Object.keys(fixedFilters.variantFilters).filter(
    (key) => !Object.keys(_searchParameters).includes(key),
  );

  newEntries.forEach((item) => {
    (_searchParameters as any)[item] = (fixedFilters.variantFilters as any)[
      item
    ];
  });

  return _searchParameters;
};

export const searchToQuery = (searchInput: SearchInput) => {
  const result: { [key: string]: string } = {};

  if (searchInput.sort) result.sort = searchInput.sort;
  if (searchInput.page) result.page = String(searchInput.page);
  if (searchInput.minPrice) result.minPrice = `${searchInput.minPrice}`;
  if (searchInput.maxPrice) result.maxPrice = `${searchInput.maxPrice}`;
  if (searchInput.query) result.query = `${searchInput.query}`;

  const { variantFilters } = searchInput;

  Object.keys(variantFilters).forEach((key) => {
    const value = variantFilters[key as keyof typeof variantFilters];

    if (value == null) return;

    if (Array.isArray(value)) result[key] = value.join('|');
  });

  return result;
};

export const useCollectionSearchApi = (
  qs: string,
  initialData: {
    collections: Array<EssentialSearchOffers>;
    products: Record<string, Product>;
    options: Options;
  },
) =>
  useAbortableGetApi<CollectionsSearchApiResponse, typeof initialData>(
    '/api/collections/search',
    qs,
    initialData,
    (data: CollectionsSearchApiResponse) => ({
      collections: data.collections,
      products: data.products,
      options: data.options,
    }),
    {
      errorMessage: 'Failed to fetch collections search results',
    },
  );

export const updateQueryString = ({
  searchInput,
  router,
  handle,
  pk,
}: {
  searchInput: SearchInput;
  router: NextRouter;
  handle?: string;
  pk?: string;
}) => {
  const desiredFilters = Object.fromEntries(
    Object.entries(searchInput.variantFilters).filter(
      ([key]) => key !== 'psku' && key !== 'categories',
    ),
  );

  const visibleSearchInput = {
    sort: searchInput.sort,
    minPrice: searchInput.minPrice,
    maxPrice: searchInput.maxPrice,
    variantFilters: desiredFilters,
  };

  // GMC: Extract UTM parameters, gclid, and gad_source from router.query
  const {
    utm_source,
    utm_medium,
    utm_campaign,
    utm_content,
    utm_term,
    gclid,
    gad_source,
  } = router.query;

  const utmParams = {
    utm_source,
    utm_medium,
    utm_campaign,
    utm_content,
    utm_term,
    gclid,
    gad_source,
  };
  // Filter out undefined UTM parameters
  const filteredUtmParams = Object.fromEntries(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Object.entries(utmParams).filter(([_, value]) => value != null),
  );

  const usp = new URLSearchParams({
    ...searchToQuery(visibleSearchInput),
    ...(handle != null ? { handle } : undefined),
    ...(pk != null ? { pk } : undefined),
    ...filteredUtmParams,
  });

  usp.sort();
  const qs = usp.toString();

  router.push(
    {
      pathname: router.pathname,
      query: qs,
    },
    undefined,
    { shallow: true },
  );
};
